import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase.js';

// AITriageChat — customer-side AI triage chat (Task #100, AI-PHASE-1).
//
// Drops in between the SubmitForm submit click and the actual submitTicket call.
// Walks the customer through 4-6 clarifying questions guided by Claude Sonnet,
// then escalates to a human technician with a clean summary attached.
//
// "Talk to a human now" button is ALWAYS visible at the top. Spec §6.4 — it
// is never hidden, never dismissable, one tap escalates.
//
// Props:
//   - skill: { id, name } selected on the previous step
//   - initialDescription: customer's opening message
//   - photoUrls: pre-uploaded photo URLs (already in Cloud Storage)
//   - assetId?: optional asset reference
//   - onEscalate(payload): user wants a human — payload contains
//       { sessionId, summary, urgency, transcript } from aiTriageEscalate
//   - onBail(): user wants to abandon the chat entirely (back to form)

export default function AITriageChat({ skill, initialDescription, photoUrls, assetId, onEscalate, onResolve, onBail }) {
  // Conversation state. Each message: { role: 'user'|'assistant', text }.
  // The opening customer description seeds the visible transcript so the
  // customer can see what the AI is reacting to.
  const [messages, setMessages] = useState([{ role: 'user', text: initialDescription }]);
  const [sessionId, setSessionId] = useState(null);
  const [pending, setPending] = useState(false);
  const [input, setInput] = useState('');
  const [readyToEscalate, setReadyToEscalate] = useState(false);
  const [autoEscalateOnNext, setAutoEscalateOnNext] = useState(false);
  const [escalating, setEscalating] = useState(false);
  // Task #102 — when the AI emits [RESOLVED], swap the input box for a Yes/No
  // confirmation. Yes calls aiTriageResolve; No falls through to escalation.
  const [resolutionPrompt, setResolutionPrompt] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [turnsRemaining, setTurnsRemaining] = useState(10);

  const scrollerRef = useRef(null);
  const startedRef = useRef(false);

  // Kick off the session on mount. Guarded by startedRef so React strict-mode
  // double-renders don't fire two aiTriageStart calls.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setPending(true);
      try {
        const start = httpsCallable(functions, 'aiTriageStart');
        const res = await start({
          skillCategoryId: skill.id,
          initialDescription,
          photoUrls: photoUrls || [],
        });
        const d = res.data || {};
        setSessionId(d.sessionId);
        setMessages((prev) => [...prev, { role: 'assistant', text: d.assistantReply || '' }]);
        setTurnsRemaining(typeof d.turnsRemaining === 'number' ? d.turnsRemaining : 10);
        if (d.shouldEscalate) {
          // Emergency-keyword or out-of-scope short-circuit. Don't auto-escalate
          // without the customer's confirmation, but light up the big button.
          setReadyToEscalate(true);
          setAutoEscalateOnNext(true);
        }
      } catch (err) {
        // AI provider unavailable / not configured / etc. — fall back to the
        // existing direct-submit flow by escalating with no AI metadata.
        setError(friendly(err));
        // Auto-bail back to the form after a short delay if there's no session.
        setTimeout(() => onBail?.(), 1500);
      } finally {
        setPending(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to the latest message whenever the transcript grows.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  async function sendReply() {
    if (!sessionId || !input.trim() || pending) return;
    const text = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setPending(true);
    setError(null);
    try {
      const reply = httpsCallable(functions, 'aiTriageReply');
      const res = await reply({ sessionId, userMessage: text });
      const d = res.data || {};
      setMessages((prev) => [...prev, { role: 'assistant', text: d.assistantReply || '' }]);
      setTurnsRemaining(typeof d.turnsRemaining === 'number' ? d.turnsRemaining : turnsRemaining);
      if (d.shouldEscalate) {
        setReadyToEscalate(true);
        setAutoEscalateOnNext(true);
      }
      // Task #102 — AI thinks the customer fixed it. Show Yes/No confirmation.
      if (d.shouldResolve) {
        setResolutionPrompt(true);
      }
    } catch (err) {
      setError(friendly(err));
    } finally {
      setPending(false);
    }
  }

  // Task #102 — customer confirms the self-fix worked. Close the session +
  // create a CLOSED ticket via aiTriageResolve, then bubble up to RequesterApp.
  async function confirmResolved() {
    if (!sessionId || resolving) return;
    setResolving(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'aiTriageResolve');
      const res = await fn({ sessionId });
      onResolve?.(res.data);
    } catch (err) {
      setError(friendly(err));
      setResolving(false);
    }
  }

  // Customer says "no, didn't fix it" — fall through to the normal escalation.
  function notResolved() {
    setResolutionPrompt(false);
    setReadyToEscalate(true);
    setAutoEscalateOnNext(true);
  }

  async function escalate() {
    if (!sessionId || escalating) return;
    setEscalating(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'aiTriageEscalate');
      const res = await fn({ sessionId });
      onEscalate?.(res.data);
    } catch (err) {
      setError(friendly(err));
      setEscalating(false);
    }
  }

  // If autoEscalateOnNext was triggered AND the customer hasn't typed, focus
  // attention on the escalate button (visual emphasis via the readyToEscalate
  // flag controlling button style).
  const escalateLabel = autoEscalateOnNext
    ? 'Yes — talk to a human now'
    : 'Talk to a human now';

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
      {/* Persistent escalate button — top, never dismissible. Spec §6.4 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, paddingBottom: 12, borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Quick check before we dispatch</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {skill.name} · {turnsRemaining} questions left
          </div>
        </div>
        <button
          type="button"
          onClick={escalate}
          disabled={escalating}
          className="btn-primary"
          style={{
            background: readyToEscalate ? '#dc2626' : 'var(--accent, #4f46e5)',
            fontSize: 13,
          }}>
          {escalating ? 'Connecting...' : escalateLabel}
        </button>
      </div>

      {/* Disclaimer — shown once at the top of the transcript */}
      <div style={{
        marginTop: 12, padding: '8px 10px',
        background: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: 6, fontSize: 12, color: '#075985',
      }}>
        I'm an AI assistant. I can help triage your issue and suggest safe checks,
        but I'm not a substitute for a qualified technician. Tap "Talk to a human now"
        anytime to dispatch.
      </div>

      {/* Task #101 (AI-PHASE-2): thumbnails of the photos the customer attached.
          Lets them see what the AI is looking at; reduces "did it see my photo?"
          confusion. */}
      {Array.isArray(photoUrls) && photoUrls.length > 0 && (
        <div style={{
          marginTop: 8, padding: '6px 8px',
          background: '#fafafa', border: '1px solid var(--border)',
          borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            AI is looking at:
          </span>
          {photoUrls.slice(0, 5).map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Customer photo ${i + 1}`}
              style={{
                width: 48, height: 48,
                objectFit: 'cover', borderRadius: 4,
                border: '1px solid var(--border)',
              }}
            />
          ))}
        </div>
      )}

      {/* Transcript */}
      <div
        ref={scrollerRef}
        style={{
          marginTop: 12, padding: 8, flex: 1, minHeight: 200,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} text={m.text} />
        ))}
        {pending && <Bubble role="assistant" text="..." />}
      </div>

      {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}

      {/* Reply form — disabled when the AI has signaled escalation or resolution */}
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        {resolutionPrompt ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Did that fix the issue?</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Tap <strong>Yes</strong> if you're good — we'll close the request without
              dispatching a technician. Tap <strong>No</strong> to bring in a human.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={confirmResolved}
                disabled={resolving}
                className="btn-primary"
                style={{ flex: 1, background: '#10b981' }}>
                {resolving ? 'Closing...' : 'Yes, all fixed'}
              </button>
              <button
                type="button"
                onClick={notResolved}
                disabled={resolving}
                className="btn-ghost"
                style={{ flex: 1 }}>
                No, still broken
              </button>
            </div>
          </div>
        ) : readyToEscalate ? (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            The AI has enough information. Tap <strong>{escalateLabel}</strong> above to dispatch.
          </div>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); sendReply(); }}
            style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your answer..."
              disabled={pending || !sessionId}
              autoFocus
              maxLength={2000}
              style={{
                flex: 1, padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
              }}
            />
            <button
              type="submit"
              disabled={pending || !sessionId || !input.trim()}
              className="btn-primary">
              Send
            </button>
          </form>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', textAlign: 'right' }}>
          <button
            type="button"
            onClick={onBail}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, padding: 0 }}>
            ← Edit my request instead
          </button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, text }) {
  const isUser = role === 'user';
  return (
    <div style={{
      alignSelf: isUser ? 'flex-end' : 'flex-start',
      maxWidth: '85%',
      padding: '8px 12px',
      borderRadius: 12,
      background: isUser ? 'var(--accent, #4f46e5)' : '#f1f5f9',
      color: isUser ? 'white' : '#0f172a',
      fontSize: 14,
      lineHeight: 1.4,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}>
      {text}
    </div>
  );
}

function friendly(err) {
  if (err?.code === 'failed-precondition') return err.message;
  if (err?.code === 'unauthenticated') return 'Please sign in again.';
  if (err?.code === 'not-found') return 'This chat session is no longer available.';
  return err?.message || 'Something went wrong. Tapping "Talk to a human now" still works.';
}
